// src/components/UserManagement/UserManagement.jsx
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import React from "react";
import { filterUsers } from "../../api/UserAPI";
import { useGlobalUI } from "../../context/GlobalUIProvider";
import { useState } from "react";
import { useEffect } from "react";
import { formatIdWithPrefix } from "../../Custom/Custom";
import { createScheduledTransactions } from "../../api/TranscationApi";

export default function Enterprise() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    currentPage: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    userType: null,
    userStatus: null,
    dob: null,
    search: "",
  });

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { showLoader, hideLoader, message } = useGlobalUI();

  const getStatusBadge = (status) => {
    const label = status === 1 || status === "Active" ? "Active" : "Suspended";
    const color = label === "Active" ? "green" : "red";
    return (
      <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
        {label}
      </Tag>
    );
  };

  // KYC Badge
  const getKycBadge = (kyc) => {
    const label =
      kyc?.status === "PENDING"
        ? "Pending"
        : kyc?.status === "APPROVED"
        ? "Approved"
        : kyc?.status === "REJECTED"
        ? "Rejected"
        : "Under Review";
    const color =
      label === "Approved"
        ? "green"
        : label === "Pending"
        ? "gold"
        : label === "Rejected"
        ? "red"
        : "default";
    return (
      <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
        {label}
      </Tag>
    );
  };

  const handleCheckboxChange = (checked, record) => {
    const { amount, date } = form.getFieldsValue();

    if (checked) {
      setSelectedUsers((prev) => [
        ...prev.filter((u) => u.userId !== record.id),
        {
          userId: record.id,
          amount: amount ? parseFloat(amount) : 0, // ✅ double
          scheduledTime: date ? date.toDate() : null, // ✅ JS Date object
        },
      ]);
    } else {
      setSelectedUsers((prev) => prev.filter((u) => u.userId !== record.id));
    }
  };

  // Check if a row is already selected
  const isChecked = (id) => selectedUsers.some((u) => u.userId === id);

  // Send to API
  const handleSend = () => {
    console.log("Final Payload:", selectedUsers);
    createScheduledTransactions(selectedUsers).then((response) => {
      if (response.data.status === 1) {
        setSelectedUsers([]);
        form.resetFields();
        message.success("Transaction Successful");
      } else {
        message.error("Failed transactions");
      }
    });
    // call API here
  };

  const fetchUsers = (page = 0, size = 10, filterData = {}) => {
    showLoader();
    return filterUsers({ pageNo: page, size, ...filterData })
      .then((res) => {
        if (res.data && res.data.data) {
          const { users, totalItems, currentPage } = res.data.data;
          setUsers(users || []);

          setPagination({
            totalItems: totalItems || 0,
            currentPage: currentPage || 0,
            pageSize: size,
          });

          return (users && users.length) || 0; // ✅ return count of users
        } else {
          setUsers([]);
          return 0; // no users found
        }
      })
      .catch((err) => {
        setUsers([]);
        return Promise.reject(err); // propagate error
      })
      .finally(() => hideLoader());
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  let columns = [
    {
      title: "Select",
      key: "select",
      render: (record) => (
        <Checkbox
          checked={isChecked(record.id)}
          onChange={(e) => handleCheckboxChange(e.target.checked, record)}
        />
      ),
    },
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (val, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{val}</span>
          <span style={{ color: "#888", fontSize: 12 }}>
            {formatIdWithPrefix(row?.userCode ?? row?.id, "USR")}
          </span>
        </Space>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (record) => (
        <>
          <div>{record.email}</div>
          <div style={{ color: "#888" }}>{record.mobileNo}</div>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "userStatus",
      key: "status",
      render: getStatusBadge,
    },
    {
      title: "KYC Status",
      dataIndex: "kyc",
      key: "kyc",
      render: getKycBadge,
    },
    {
      title: "Wallet Balance",
      dataIndex: ["wallet", "totalBalance"],
      key: "walletBalance",
      render: (balance) =>
        typeof balance === "number"
          ? `$${balance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}`
          : "$0.00",
    },
    {
      title: "Join Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date) => date?.slice(0, 10),
    },
  ];

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        {/* Left Section */}
        <Col>
          <div style={{ fontSize: 28, fontWeight: 600 }}>Bulk Transaction</div>
          <div style={{ color: "#555" }}>Manage bulk transaction</div>
        </Col>
        <Col>
          <Form form={form} layout="inline">
            {/* Amount Field */}
            <Form.Item label="Amount" name="amount">
              <Input placeholder="Enter amount" style={{ width: 150 }} />
            </Form.Item>

            {/* Date Field */}
            <Form.Item label="Date" name="date">
              <DatePicker style={{ width: 180 }} />
            </Form.Item>

            {/* Send Button */}
            <Form.Item>
              <Button type="primary" onClick={handleSend}>
                Send
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {/* Table Card */}
      <Card bodyStyle={{ padding: 0 }}>
        <div style={{ padding: "16px 24px 0 24px" }}>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <div style={{ fontSize: 20, fontWeight: 600 }}>All Users</div>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.currentPage + 1,
            pageSize: pagination.pageSize,
            total: pagination.totalItems,
            onChange: (page) =>
              fetchUsers(page - 1, pagination.pageSize, filters),
          }}
          style={{ borderRadius: 12, margin: "12px" }}
        />
      </Card>
    </div>
  );
}
